import { motion, useReducedMotion } from "framer-motion";
import { Apple, Heart, Flame, Shield, Droplets, Waves, Leaf, Activity, Moon, Brain } from "lucide-react";

const NODES = [
  { icon: Apple, label: "Nutrition", color: "#1F4D3A" },
  { icon: Heart, label: "Meaningful Connections", color: "#2D8C83" },
  { icon: Flame, label: "Metabolic Health", color: "#1F4D3A" },
  { icon: Shield, label: "Immune Health", color: "#B38B3D" },
  { icon: Droplets, label: "Hormonal Balance", color: "#B38B3D" },
  { icon: Waves, label: "Gut Health", color: "#2D8C83" },
  { icon: Leaf, label: "Detox & Environment", color: "#1F4D3A" },
  { icon: Activity, label: "Movement", color: "#B38B3D" },
  { icon: Moon, label: "Sleep", color: "#2D8C83" },
  { icon: Brain, label: "Stress Resilience", color: "#1F4D3A" },
];

const RADIUS = 41;
const SPIN = 130;

const Node = ({ node, deg, index, reduce }) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  const x = 50 + RADIUS * Math.cos(rad);
  const y = 50 + RADIUS * Math.sin(rad);
  const Icon = node.icon;
  return (
    <div className="absolute" style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}>
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={reduce ? { scale: 1, opacity: 1 } : { scale: 1, opacity: 1, rotate: -360 }}
        transition={{
          scale: { duration: 0.6, delay: 0.5 + index * 0.07, ease: [0.16, 1, 0.3, 1] },
          opacity: { duration: 0.6, delay: 0.5 + index * 0.07 },
          rotate: { duration: SPIN, repeat: Infinity, ease: "linear" },
        }}
        className="flex flex-col items-center group"
      >
        <div
          className="w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(31,77,58,0.4)] transition-transform duration-300 group-hover:scale-110"
          style={{ backgroundColor: node.color }}
        >
          <Icon className="text-ivory w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.6} />
        </div>
        <span className="hidden sm:block mt-1.5 font-mono text-[8px] tracking-[0.12em] uppercase text-inkmuted text-center leading-tight max-w-[88px]">
          {node.label}
        </span>
      </motion.div>
    </div>
  );
};

export const WellbeingWheel = () => {
  const reduce = useReducedMotion();
  return (
    <div className="relative aspect-square w-full select-none" data-testid="wellbeing-wheel">
      {/* soft radial glow */}
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 50%, rgba(179,139,61,0.14) 0%, rgba(247,244,237,0) 62%)" }}
        aria-hidden="true"
      />

      {/* sun rays */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" aria-hidden="true">
        {NODES.map((_, i) => {
          const a = ((i * 36 - 90) * Math.PI) / 180;
          return (
            <line
              key={i}
              x1={50 + 20 * Math.cos(a)}
              y1={50 + 20 * Math.sin(a)}
              x2={50 + 33 * Math.cos(a)}
              y2={50 + 33 * Math.sin(a)}
              stroke="#B38B3D"
              strokeOpacity="0.35"
              strokeWidth="0.25"
            />
          );
        })}
      </svg>

      {/* outer dashed ring, slow counter-spin */}
      <motion.svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        aria-hidden="true"
        animate={reduce ? {} : { rotate: -360 }}
        transition={{ duration: 200, repeat: Infinity, ease: "linear" }}
        style={{ originX: "50%", originY: "50%" }}
      >
        <circle cx="50" cy="50" r="48.5" fill="none" stroke="#B38B3D" strokeOpacity="0.5" strokeWidth="0.3" strokeDasharray="1.2 2.4" />
        <circle cx="50" cy="50" r="35" fill="none" stroke="#2D8C83" strokeOpacity="0.3" strokeWidth="0.25" strokeDasharray="0.6 2" />
      </motion.svg>

      {/* orbiting pillar nodes */}
      <motion.div
        className="absolute inset-0"
        animate={reduce ? {} : { rotate: 360 }}
        transition={{ duration: SPIN, repeat: Infinity, ease: "linear" }}
      >
        {NODES.map((n, i) => (
          <Node key={n.label} node={n} deg={i * 36} index={i} reduce={reduce} />
        ))}
      </motion.div>

      {/* center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <motion.div
          className="absolute -inset-5 sm:-inset-7 rounded-full bg-gold/15"
          aria-hidden="true"
          animate={reduce ? {} : { scale: [1, 1.18, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-28 h-28 sm:w-40 sm:h-40 rounded-full bg-ivory border border-gold/50 shadow-[0_14px_40px_-14px_rgba(31,77,58,0.35)] flex flex-col items-center justify-center text-center px-3"
        >
          <span className="font-serif text-xl sm:text-3xl text-forest leading-[1.05]">
            Well
            <br />
            Being
          </span>
          <span className="mt-1.5 font-mono text-[7px] sm:text-[9px] tracking-[0.2em] uppercase text-gold">
            A healthier, brighter you
          </span>
        </motion.div>
      </div>
    </div>
  );
};
